import { createFileRoute, isRedirect, redirect } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { LayoutAuth } from "#/layouts/layout.auth";
import { acceptInviteExistingFn } from "#/modules/generic.invite/invite.functions";
import { useAppForm } from "#/modules/util.forms/form";
import { op } from "#/modules/util.instrumentation/op";
import MaterialSymbolsCheckCircleOutline from "~icons/material-symbols/check-circle-outline";
import { z } from "zod/v4";

const existingInviteSchema = z.object({
  inviteId: z.string().min(1),
  token: z.string().min(1),
});

export const Route = createFileRoute("/$locale/{-$schoolyear}/invite/existing")({
  validateSearch: z.object({
    inviteId: z.string().catch(""),
    token: z.string().catch(""),
    schoolName: z.string().catch(""),
    inviteeEmail: z.string().catch(""),
  }),
  beforeLoad: ({ search, params }) => {
    if (!search.inviteId || !search.token || !search.schoolName || !search.inviteeEmail) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/login",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
  },
  component: InviteExistingPage,
});

function InviteExistingPage() {
  const { inviteId, token, schoolName, inviteeEmail } = Route.useSearch();
  const acceptInviteExisting = useServerFn(acceptInviteExistingFn);

  const form = useAppForm({
    defaultValues: { inviteId, token },
    validators: { onSubmit: existingInviteSchema },
    onSubmit: async () => {
      try {
        const res = await acceptInviteExisting({ data: { inviteId, token } });
        if (res) {
          form.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
          return;
        }
        // On success, acceptInviteExistingFn throws a redirect so we never reach here
        void op.track("invite_accepted", { existing_account: true });
      } catch (error) {
        if (isRedirect(error)) {
          void op.track("invite_accepted", { existing_account: true });
          throw error;
        }
        captureUnexpectedError(error, "invite-existing");
        form.setErrorMap({ onSubmit: { form: m["common.something_went_wrong"](), fields: {} } });
      }
    },
  });

  return (
    <LayoutAuth showVisuals={false} containerClassName="max-w-lg w-full mb-[5%]">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>
            <span className="font-semibold">{schoolName}</span> nodigt je uit!
          </CardTitle>
          <CardDescription>Je hebt al een account met {inviteeEmail}</CardDescription>
        </CardHeader>
        <CardContent>
          <form.AppForm>
            <form.FormRoot className="flex flex-col gap-4">
              <form.SubmitButton className="w-full">
                <MaterialSymbolsCheckCircleOutline className="mr-2 h-5 w-5" />
                Accepteer uitnodiging
              </form.SubmitButton>
            </form.FormRoot>
          </form.AppForm>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
